// 3rd party imports
import * as React from 'react';
import { Link, Redirect, withRouter } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { RouteComponentProps } from 'react-router';

// ui
import { UitkSubheading, UitkHeading } from 'uitk-react-text';
import { UitkCard } from 'uitk-react-cards';
import { UitkSpacing, UitkSpacingHr } from 'uitk-react-spacing';
import { UitkLink } from 'uitk-react-link';
import { UitkLayoutFlex } from 'uitk-react-layout-flex';
import { UitkPrimaryButton, UitkSecondaryButton, UitkButtonSize } from 'uitk-react-button';
import { UitkList, UitkListItem } from 'uitk-react-list';
import { UitkFakeInput } from 'uitk-react-input';

// bernie
import { AnalyticsStore, ContextStore } from 'bernie-core';
import { LocalizedText } from 'bernie-l10n';
import { ILocalization, withLocalization } from '@shared-ui/localization-context';

// components
import { LocalizedTextWrapping } from 'components/localized-text-wrapping';

// util
import { modifyLocaleParam } from '../../util/LocaleParamTransformer';
import { getUserAnalyticsMicroMessage } from '../../util/analytics';
import { getPartnerizeTrackingLink } from '../../util/tracking';

// mobx
import { UserAuthStore } from 'stores';

export interface JoinProgramConfProps extends RouteComponentProps {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  l10n?: ILocalization;
  userAuth?: UserAuthStore;
}

export interface JoinProgramConfState {
  copied: boolean;
}

@withLocalization
@inject('analytics', 'context', 'l10n', 'userAuth')
@observer
export class ConfirmationPage extends React.Component<JoinProgramConfProps, JoinProgramConfState> {
  public constructor(props) {
    super(props);
    this.state = {
      copied: false,
    };
  }

  private partnerizeSiteLink = 'https://console.partnerize.com/login/phg/en';
  private zendeskLink =
    'https://egaffiliates.zendesk.com/hc/en-us/articles/1500003709001-FAQ-Partnerize-and-the-Affiliate-Hub';
  private moreLinks = '/quicklinks?programName=';
  private viewProductsLink = '/products';

  private setTimer = () => {
    this.setState({
      copied: true,
    });
    setTimeout(() => {
      this.setState({
        copied: false,
      });
    }, 2000);
  };

  private copyBtnOnClick = (copy) => {
    const { analytics, userAuth } = this.props;
    if (typeof analytics?.trackEvent === 'function') {
      analytics.trackEvent(
        'affhub_confirmation_copy_link.selected',
        'Affhub Confirmation Copy Link Selected',
        null,
        null,
        true,
        [getUserAnalyticsMicroMessage(userAuth?.currentUserDetails)],
      );
    }

    navigator.clipboard.writeText(copy);
    this.setTimer();
  };

  public render() {
    const locale = modifyLocaleParam(this.props.context.locale);

    let camRef = null;
    let partnerId = null;
    let program = null;
    let existingRegistration = null;

    if (this.props.location.state) {
      camRef = this.props.location.state['camRef'];
      partnerId = this.props.location.state['partnerId'];
      program = this.props.location.state['program'];
      existingRegistration = this.props.location.state['existingRegistration'];
    }

    if (camRef == null || partnerId == null) {
      return <Redirect to={`/${locale}/home`} />;
    }

    const trackingLink = getPartnerizeTrackingLink({
      baseUrl: program.networkTrackingBaseUrl,
      camRef,
      creativeRef: program.creativeRef,
      destination: program.siteUrl,
    });

    // labels
    const trackingLinkLabel = this.props.l10n.formatText('pz.signup.post_registration.section.tracking_link.label');
    const trackingLinkCopyLabel = this.props.l10n.formatText('common.copy');
    const trackingLinkCopiedLabel = this.props.l10n.formatText('common.copied');

    return (
      <>
        <UitkSpacing className="container" padding={{ block: 'twelve' }}>
          <main>
            <UitkSpacing margin={{ blockend: 'six' }} className="JoinProgramConfirmation__title">
              <UitkHeading tag="h1" size={2}>
                {!existingRegistration && (
                  <LocalizedText
                    message="pz.signup.post_registration.title.your_partnerize_account_has_been_created_and_you_are_approved_for_the_program"
                    data={[program.title]}
                  />
                )}
                {existingRegistration && (
                  <LocalizedText
                    message="pz.signup.post_registration.title.you_have_been_approved_for_program"
                    data={[program.title]}
                  />
                )}
              </UitkHeading>
            </UitkSpacing>

            <UitkSpacing margin={{ blockstart: 'two', blockend: 'six' }}>
              <UitkCard overflow={false} padded={true} className="JoinProgramConfirmation__hero">
                <UitkSpacing margin={{ blockend: 'four' }}>
                  <div>
                    <UitkHeading tag="h2" size={5}>
                      <LocalizedText message="pz.signup.post_registration.section.pz_registration_details.your_partnerize_partner_id_is" />
                    </UitkHeading>
                    <UitkSubheading tag="h4">{partnerId}</UitkSubheading>
                  </div>
                </UitkSpacing>
                <div>
                  <UitkHeading tag="h2" size={5}>
                    <LocalizedText
                      message="pz.signup.post_registration.section.pz_registration_details.your_vrbo_program_tracking_code_camref_is"
                      data={[program.title]}
                    />
                  </UitkHeading>
                  <UitkSubheading tag="h4">{camRef}</UitkSubheading>
                </div>
              </UitkCard>
            </UitkSpacing>

            <UitkSpacingHr />

            <UitkSpacing margin={{ blockstart: 'twelve', blockend: 'four' }}>
              <UitkHeading tag="h1" size={4}>
                <LocalizedText message="pz.signup.post_registration.section.tracking_link.title.your_basic_tracking_link_is" />
              </UitkHeading>
            </UitkSpacing>

            <UitkSpacing margin={{ blockend: 'six' }}>
              <div>
                <UitkLayoutFlex space="two" className="JoinProgramConfirmation__copy-input">
                  <UitkFakeInput label={trackingLinkLabel} value={trackingLink} />
                  <UitkSecondaryButton
                    className="JoinProgramConfirmation__copy-btn"
                    onClick={() => this.copyBtnOnClick(trackingLink)}
                    size={UitkButtonSize.LARGE}
                  >
                    {this.state.copied ? trackingLinkCopiedLabel : trackingLinkCopyLabel}
                  </UitkSecondaryButton>
                </UitkLayoutFlex>
              </div>
            </UitkSpacing>

            <UitkSpacing margin={{ blockend: 'six' }}>
              <UitkPrimaryButton type="button" tag={'a'} href={`/${locale}${this.moreLinks}${program.name}`}>
                <LocalizedText message="pz.signup.post_registration.section.tracking_link.button.see_more_links_configure_your_own" />
              </UitkPrimaryButton>
            </UitkSpacing>

            <UitkSpacing margin={{ blockstart: 'six', blockend: 'two' }}>
              <UitkHeading tag="h1" size={4}>
                <LocalizedText message="pz.signup.post_registration.section.next_steps.title.next_steps" />
              </UitkHeading>
            </UitkSpacing>

            <UitkList type="ordered" className="JoinProgramConfirmation__steps">
              <UitkListItem>
                <LocalizedTextWrapping
                  message="pz.signup.post_registration.section.next_steps.go_to_products_for_tracked_link"
                  fieldName="products"
                >
                  <UitkLink inline={true} data-stid="go-to-products">
                    <Link to={`/${locale}${this.viewProductsLink}?programName=${program.name}`} target="_self">
                      <LocalizedText message="pz.signup.post_registration.section.next_steps.products" />
                    </Link>
                  </UitkLink>
                </LocalizedTextWrapping>
              </UitkListItem>
              <UitkListItem>
                <LocalizedTextWrapping
                  message="pz.signup.post_registration.section.next_steps.log_into_partnerize_add_your_bank_details"
                  fieldName="partnerize"
                >
                  <UitkLink inline={true}>
                    <a href={this.partnerizeSiteLink} target="_blank">
                      <LocalizedText message="pz.signup.post_registration.section.next_steps.partnerize" />
                    </a>
                  </UitkLink>
                </LocalizedTextWrapping>
                <br />
                <LocalizedText message="pz.signup.post_registration.section.next_steps.you_should_have_received_partnerize_welcome_email" />
              </UitkListItem>
              <UitkListItem>
                <LocalizedTextWrapping
                  message="pz.signup.post_registration.section.next_steps.find_answers_to_common_questions"
                  fieldName="here"
                >
                  <UitkLink inline={true}>
                    <a href={this.zendeskLink} target="_blank">
                      <LocalizedText message="pz.signup.post_registration.section.next_steps.here" />
                    </a>
                  </UitkLink>
                </LocalizedTextWrapping>
                <br />
              </UitkListItem>
            </UitkList>
          </main>
        </UitkSpacing>
      </>
    );
  }
}

export const JoinProgramConfirmation = withRouter(ConfirmationPage);
